import { useState, useEffect } from "react";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { CheckCircle2, Upload, X, ChevronLeft, ChevronRight, Check, Trash2, Tag } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  SERVICES, TIME_SLOTS, BIN_TYPES, BIN_COUNTS, BIN_FREQUENCIES, BINS_SERVICE_TITLE,
} from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  service_type: "",
  custom_service_name: "",
  custom_service_desc: "",
  date: null,
  time_slot: "",
  first_name: "",
  last_name: "",
  address: "",
  city: "Québec",
  postal_code: "",
  phone: "",
  email: "",
  surface: "",
  dimensions: "",
  dirt_level: "",
  oil_or_rust: "non",
  bin_count: "",
  bin_types: [],
  frequency: "une_fois",
  comments: "",
  photos: [],
  promo_code: "",
  consent: false,
};

const STEPS_LABELS = ["Service", "Date & heure", "Détails", "Coordonnées"];

export default function Booking() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialState);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState({ name: "", desc: "" });
  const [promoStatus, setPromoStatus] = useState(null); // {valid, message}
  const [promoChecking, setPromoChecking] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const isBins = data.service_type === BINS_SERVICE_TITLE;

  const toggleBinType = (value) =>
    setData((d) => ({
      ...d,
      bin_types: d.bin_types.includes(value)
        ? d.bin_types.filter((v) => v !== value)
        : [...d.bin_types, value],
    }));

  const pickService = (title, id) => {
    if (id === "autre") {
      setCustomDraft({ name: data.custom_service_name || "", desc: data.custom_service_desc || "" });
      setCustomDialogOpen(true);
    } else {
      set("service_type", title);
      set("custom_service_name", "");
      set("custom_service_desc", "");
    }
  };

  const confirmCustomService = () => {
    if (!customDraft.name.trim()) {
      toast.error("Veuillez indiquer le nom du service.");
      return;
    }
    setData((d) => ({
      ...d,
      service_type: `Autre — ${customDraft.name.trim()}`,
      custom_service_name: customDraft.name.trim(),
      custom_service_desc: customDraft.desc.trim(),
    }));
    setCustomDialogOpen(false);
  };

  useEffect(() => {
    if (!data.date) { setBookedSlots([]); return; }
    const dateStr = format(data.date, "yyyy-MM-dd");
    axios.get(`${API}/booked-slots?date=${dateStr}`)
      .then((r) => setBookedSlots(r.data.booked || []))
      .catch(() => setBookedSlots([]));
  }, [data.date]);

  useEffect(() => {
    const code = data.promo_code.trim();
    if (!code) { setPromoStatus(null); setPromoChecking(false); return; }
    setPromoChecking(true);
    const t = setTimeout(() => {
      axios.post(`${API}/promo/validate`, { code })
        .then((r) => setPromoStatus(r.data))
        .catch(() => setPromoStatus({ valid: false, message: "Impossible de vérifier le code." }))
        .finally(() => setPromoChecking(false));
    }, 500);
    return () => clearTimeout(t);
  }, [data.promo_code]);

  const canNext = () => {
    if (step === 0) return !!data.service_type;
    if (step === 1) return !!data.date && !!data.time_slot;
    if (step === 2) return isBins ? !!data.bin_count && data.bin_types.length > 0 : data.photos.length > 0;
    return false;
  };

  const handlePhotos = async (files) => {
    const list = Array.from(files).slice(0, 10 - data.photos.length);
    const toBase64 = (file) => new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
    const encoded = await Promise.all(list.map(toBase64));
    set("photos", [...data.photos, ...encoded].slice(0, 10));
  };

  const removePhoto = (i) => set("photos", data.photos.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!data.consent) { toast.error("Vous devez accepter les conditions."); return; }
    setSubmitting(true);
    try {
      const commentsWithCustom = data.custom_service_desc
        ? `[Service demandé: ${data.custom_service_name}]\n${data.custom_service_desc}${data.comments ? "\n\n" + data.comments : ""}`
        : data.comments;
      const payload = {
        ...data,
        comments: commentsWithCustom,
        date: format(data.date, "yyyy-MM-dd"),
        ...(isBins
          ? { surface: "", dirt_level: "", oil_or_rust: "" }
          : { bin_count: "", bin_types: [], frequency: "" }),
      };
      delete payload.custom_service_name;
      delete payload.custom_service_desc;
      const { data: booking } = await axios.post(`${API}/bookings`, payload);
      setSuccess(booking);
      toast.success("Réservation confirmée !");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Erreur lors de la réservation.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => { setSuccess(null); setData(initialState); setStep(0); setPromoStatus(null); };

  if (success) {
    return (
      <section id="reservation" data-testid="booking-section" className="section-y bg-accent/30">
        <div className="container-x max-w-2xl">
          <div className="card-elevated p-10 text-center" data-testid="booking-success">
            <div className="w-16 h-16 rounded-full bg-primary/10 grid place-items-center mx-auto mb-6">
              <CheckCircle2 className="w-9 h-9 text-primary" />
            </div>
            <h2 className="text-3xl font-semibold text-secondary mb-3">Réservation confirmée !</h2>
            <p className="text-muted-foreground mb-8">
              Merci {success.first_name}. Nous vous contacterons bientôt pour confirmer votre rendez-vous du{" "}
              <strong className="text-secondary">{success.date} à {success.time_slot}</strong>.
            </p>
            <div className="text-sm text-muted-foreground mb-8">
              Numéro de confirmation : <span className="font-mono text-secondary">{success.id.slice(0, 8).toUpperCase()}</span>
            </div>
            {success.promo_code && (
              <div className="text-sm text-emerald-600 font-medium mb-8 -mt-6" data-testid="booking-success-promo">
Code promotionnel {success.promo_code} appliqué — rabais de {success.promo_discount} $
              </div>
            )}
            <Button onClick={reset} data-testid="booking-new" className="bg-primary hover:bg-primary/90 rounded-xl">
              Nouvelle réservation
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="reservation" data-testid="booking-section" className="section-y bg-accent/30">
      <div className="container-x max-w-4xl">
        <div className="max-w-2xl mb-10">
          <p className="overline mb-3">Réservation en ligne</p>
          <h2 className="text-3xl sm:text-4xl font-semibold text-secondary tracking-tight">
            Réservez votre lavage en quelques minutes.
          </h2>
        </div>

        <div className="card-elevated p-6 sm:p-10">
          {/* Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              {STEPS_LABELS.map((l, i) => (
                <div key={l} className={`text-xs sm:text-sm font-medium ${i <= step ? "text-primary" : "text-muted-foreground"}`}>
                  <span className="hidden sm:inline">{i + 1}. </span>{l}
                </div>
              ))}
            </div>
            <Progress value={((step + 1) / STEPS_LABELS.length) * 100} className="h-2" />
          </div>

          {/* Step 0: Service */}
          {step === 0 && (
            <div data-testid="booking-step-service">
              <h3 className="text-xl font-semibold text-secondary mb-6">Choisissez votre service</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {SERVICES.map((s) => {
                  const isSelected =
                    (s.id === "autre" && data.service_type.startsWith("Autre")) ||
                    (s.id !== "autre" && data.service_type === s.title);
                  return (
                    <button
                      type="button"
                      key={s.id}
                      data-testid={`booking-service-${s.id}`}
                      onClick={() => pickService(s.title, s.id)}
                      className={`text-left p-5 rounded-xl border-2 transition-all ${
                        isSelected
                          ? "border-primary bg-accent"
                          : "border-gray-100 hover:border-primary/40 bg-white"
                      }`}
                    >
                      <div className="font-semibold text-secondary flex items-center justify-between gap-2">
                        <span>{s.title}</span>
                        {s.id === "autre" && data.custom_service_name && (
                          <span className="text-xs font-normal text-primary truncate max-w-[50%]">
                            {data.custom_service_name}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {s.id === "autre" && data.custom_service_desc ? data.custom_service_desc : s.desc}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 1: Date & time */}
          {step === 1 && (
            <div data-testid="booking-step-date">
              <h3 className="text-xl font-semibold text-secondary mb-6">Choisissez la date et l'heure</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="rounded-xl border border-gray-100 p-3 bg-white">
                  <Calendar
                    mode="single"
                    selected={data.date}
                    onSelect={(d) => set("date", d)}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                    locale={fr}
                    data-testid="booking-calendar"
                  />
                </div>
                <div>
                  <div className="text-sm font-medium text-secondary mb-3">
                    {data.date ? `Créneaux du ${format(data.date, "d MMMM yyyy", { locale: fr })}` : "Sélectionnez d'abord une date"}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {TIME_SLOTS.map((t) => {
                      const taken = bookedSlots.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          disabled={!data.date || taken}
                          data-testid={`booking-slot-${t}`}
                          onClick={() => set("time_slot", t)}
                          className={`py-3 rounded-xl text-sm font-medium border-2 transition-all ${
                            data.time_slot === t
                              ? "border-primary bg-primary text-white"
                              : taken
                              ? "border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed line-through"
                              : "border-gray-100 hover:border-primary bg-white text-secondary"
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div data-testid="booking-step-details">
              <h3 className="text-xl font-semibold text-secondary mb-6">
                {isBins ? "Détails de vos bacs" : "Détails du service"}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {isBins ? (
                  <>
                    <div className="sm:col-span-2">
                      <Label className="mb-3 block">Nombre de bacs à laver *</Label>
                      <div className="flex flex-wrap gap-3">
                        {BIN_COUNTS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            data-testid={`booking-bin-count-${c}`}
                            onClick={() => set("bin_count", c)}
                            className={`w-14 h-14 rounded-xl text-sm font-semibold border-2 transition-all ${
                              data.bin_count === c
                                ? "border-primary bg-primary text-white"
                                : "border-gray-100 bg-white text-secondary hover:border-primary/40"
                            }`}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <Label className="mb-3 block">Type(s) de bacs * (plusieurs choix possibles)</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {BIN_TYPES.map((t) => {
                          const active = data.bin_types.includes(t.value);
                          return (
                            <button
                              key={t.value}
                              type="button"
                              data-testid={`booking-bin-type-${t.value}`}
                              onClick={() => toggleBinType(t.value)}
                              className={`flex items-center gap-3 p-4 rounded-xl border-2 text-sm font-medium transition-all ${
                                active
                                  ? "border-primary bg-accent text-secondary"
                                  : "border-gray-100 bg-white text-secondary hover:border-primary/40"
                              }`}
                            >
                              <span className={`w-5 h-5 rounded-md grid place-items-center border-2 transition-colors ${active ? "bg-primary border-primary" : "border-gray-300"}`}>
                                {active && <Check className="w-3 h-3 text-white" />}
                              </span>
                              <Trash2 className={`w-4 h-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
                              {t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <Label className="mb-2 block">Fréquence du service</Label>
                      <Select value={data.frequency} onValueChange={(v) => set("frequency", v)}>
                        <SelectTrigger data-testid="booking-frequency" className="bg-white"><SelectValue placeholder="Choisir" /></SelectTrigger>
                        <SelectContent>
                          {BIN_FREQUENCIES.map((f) => (
                            <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-2">
                        Les services récurrents bénéficient d'un tarif préférentiel — nous vous confirmerons les détails.
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                <div>
                  <Label className="mb-2 block">Surface approximative</Label>
                  <Input data-testid="booking-surface" value={data.surface} onChange={(e) => set("surface", e.target.value)} placeholder="Ex: 40 m²" className="bg-white" />
                </div>
                <div>
                  <Label className="mb-2 block">Niveau de saleté</Label>
                  <Select value={data.dirt_level} onValueChange={(v) => set("dirt_level", v)}>
                    <SelectTrigger data-testid="booking-dirt" className="bg-white"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="leger">Léger</SelectItem>
                      <SelectItem value="modere">Modéré</SelectItem>
                      <SelectItem value="important">Important</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-2 block">Présence d'huile ou rouille ?</Label>
                  <Select value={data.oil_or_rust} onValueChange={(v) => set("oil_or_rust", v)}>
                    <SelectTrigger data-testid="booking-oil" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="non">Non</SelectItem>
                      <SelectItem value="huile">Huile</SelectItem>
                      <SelectItem value="rouille">Rouille</SelectItem>
                      <SelectItem value="deux">Les deux</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                  </>
                )}
                <div className="sm:col-span-2">
                  <Label className="mb-2 block">Commentaires</Label>
                  <Textarea data-testid="booking-comments" rows={3} value={data.comments} onChange={(e) => set("comments", e.target.value)} placeholder="Précisions utiles..." className="bg-white" />
                </div>

                {/* Photo upload */}
                <div className="sm:col-span-2">
                  <Label className="mb-2 block">
                    Photos {isBins ? "(jusqu'à 10)" : "* (au moins 1, jusqu'à 10)"}
                  </Label>
                  {!isBins && data.photos.length === 0 && (
                    <p className="text-xs text-muted-foreground mb-2" data-testid="booking-photos-required-hint">
                      Au moins une photo de la surface à nettoyer est requise pour vous fournir une soumission précise.
                    </p>
                  )}
                  <label
                    htmlFor="photo-upload"
                    data-testid="booking-photo-drop"
                    className="flex flex-col items-center justify-center border-2 border-dashed border-gray-200 rounded-xl p-8 bg-white hover:border-primary/40 cursor-pointer transition-colors"
                  >
                    <Upload className="w-8 h-8 text-primary mb-2" />
                    <span className="text-sm text-secondary font-medium">Cliquez pour ajouter vos photos</span>
                    <span className="text-xs text-muted-foreground mt-1">{data.photos.length}/10 photos</span>
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => handlePhotos(e.target.files)}
                    />
                  </label>
                  {data.photos.length > 0 && (
                    <div className="mt-4 grid grid-cols-4 sm:grid-cols-5 gap-3">
                      {data.photos.map((src, i) => (
                        <div key={i} className="relative aspect-square rounded-xl overflow-hidden">
                          <img src={src} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removePhoto(i)}
                            data-testid={`booking-photo-remove-${i}`}
                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white grid place-items-center hover:bg-black"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Contact */}
          {step === 3 && (
            <div data-testid="booking-step-contact">
              <h3 className="text-xl font-semibold text-secondary mb-6">Vos coordonnées</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className="mb-2 block">Prénom *</Label>
                  <Input data-testid="booking-first-name" value={data.first_name} onChange={(e) => set("first_name", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <Label className="mb-2 block">Nom *</Label>
                  <Input data-testid="booking-last-name" value={data.last_name} onChange={(e) => set("last_name", e.target.value)} className="bg-white" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-2 block">Adresse complète *</Label>
                  <Input data-testid="booking-address" value={data.address} onChange={(e) => set("address", e.target.value)} placeholder="Ex: 123 rue Principale" className="bg-white" />
                </div>
                <div>
                  <Label className="mb-2 block">Ville *</Label>
                  <Input data-testid="booking-city" value={data.city} onChange={(e) => set("city", e.target.value)} className="bg-white" />
                </div>
                <div>
                  <Label className="mb-2 block">Code postal *</Label>
                  <Input data-testid="booking-postal" value={data.postal_code} onChange={(e) => set("postal_code", e.target.value)} placeholder="G1A 1A1" className="bg-white" />
                </div>
                <div>
                  <Label className="mb-2 block">Téléphone *</Label>
                  <Input data-testid="booking-phone" value={data.phone} onChange={(e) => set("phone", e.target.value)} placeholder="418 555-0123" className="bg-white" />
                </div>
                <div>
                  <Label className="mb-2 block">Courriel *</Label>
                  <Input type="email" data-testid="booking-email" value={data.email} onChange={(e) => set("email", e.target.value)} placeholder="vous@courriel.com" className="bg-white" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="mb-2 block">Code promotionnel (optionnel)</Label>
                  <div className="relative">
                    <Tag className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      data-testid="booking-promo-code"
                      value={data.promo_code}
                      onChange={(e) => set("promo_code", e.target.value.toUpperCase())}
                      className="bg-white pl-9 uppercase tracking-wider"
                    />
                  </div>
                  {promoChecking && data.promo_code.trim() && (
                    <p className="text-xs text-muted-foreground mt-2" data-testid="booking-promo-checking">
                      Vérification du code...
                    </p>
                  )}
                  {!promoChecking && promoStatus && data.promo_code.trim() && (
                    <p
                      data-testid={promoStatus.valid ? "booking-promo-valid" : "booking-promo-invalid"}
                      className={`text-sm mt-2 flex items-center gap-2 font-medium ${
                        promoStatus.valid ? "text-emerald-600" : "text-red-600"
                      }`}
                    >
                      {promoStatus.valid ? <CheckCircle2 className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      {promoStatus.message}
                    </p>
                  )}
                </div>

                <div className="sm:col-span-2 flex items-start gap-3 mt-2">
                  <Checkbox
                    id="consent"
                    data-testid="booking-consent"
                    checked={data.consent}
                    onCheckedChange={(v) => set("consent", !!v)}
                  />
                  <label htmlFor="consent" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                    J'accepte les conditions générales et j'autorise Pro-Jet Nettoyage à me contacter concernant ma demande.
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-100">
            {step > 0 ? (
              <Button
                variant="ghost"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                data-testid="booking-prev"
                className="text-secondary"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
              </Button>
            ) : (
              <span />
            )}
            {step < 3 ? (
              <Button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext()}
                data-testid="booking-next"
                className="bg-primary hover:bg-primary/90 rounded-xl px-6"
              >
                Continuer <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={submitting || !data.consent || !data.first_name || !data.last_name || !data.email || !data.phone || !data.address || !data.postal_code}
                data-testid="booking-submit"
                className="bg-primary hover:bg-primary/90 rounded-xl px-6"
              >
                {submitting ? "Envoi..." : "Confirmer la réservation"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Custom "Autre" service dialog */}
      <Dialog open={customDialogOpen} onOpenChange={setCustomDialogOpen}>
        <DialogContent data-testid="custom-service-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Service personnalisé</DialogTitle>
            <DialogDescription>
              Décrivez le service dont vous avez besoin. Nous vous répondrons avec une proposition sur mesure.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="custom-name" className="mb-2 block">Nom du service *</Label>
              <Input
                id="custom-name"
                data-testid="custom-service-name"
                value={customDraft.name}
                onChange={(e) => setCustomDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="Ex: Lavage de toiture, mobilier extérieur..."
              />
            </div>
            <div>
              <Label htmlFor="custom-desc" className="mb-2 block">Courte explication</Label>
              <Textarea
                id="custom-desc"
                data-testid="custom-service-desc"
                rows={4}
                value={customDraft.desc}
                onChange={(e) => setCustomDraft((d) => ({ ...d, desc: e.target.value }))}
                placeholder="Précisez le contexte, les surfaces concernées, les particularités..."
              />
            </div>
          </div>
          <DialogFooter className="flex-row justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => setCustomDialogOpen(false)}
              data-testid="custom-service-cancel"
            >
              Annuler
            </Button>
            <Button
              onClick={confirmCustomService}
              data-testid="custom-service-confirm"
              className="bg-primary hover:bg-primary/90"
            >
              Valider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
